import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { CalendarOff, Check, X, Clock, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeaveRequests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ start_date: '', end_date: '', leave_type: 'annual', reason: '' });
  const [filter, setFilter] = useState('all');
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const isCaregiver = user?.role === 'caregiver';

  useEffect(() => {
    fetchRequests();
    if (isCaregiver) fetchBalance();
  }, [user]);

  const fetchRequests = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/leave-requests`, { withCredentials: true });
      setRequests(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchBalance = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/leave-requests/balance`, { withCredentials: true });
      setBalance(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) return toast.error('Dates required');
    try {
      await axios.post(`${API_URL}/api/leave-requests`, form, { withCredentials: true });
      toast.success('Leave request submitted');
      setDialogOpen(false);
      setForm({ start_date: '', end_date: '', leave_type: 'annual', reason: '' });
      fetchRequests();
      fetchBalance();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.put(`${API_URL}/api/leave-requests/${id}/${action}`, {}, { withCredentials: true });
      toast.success(`Leave ${action === 'approve' ? 'approved' : 'denied'}`);
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const statusColors = { pending: 'text-amber-400 bg-amber-500/10', approved: 'text-emerald-400 bg-emerald-500/10', denied: 'text-red-400 bg-red-500/10' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="leave-requests-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              <CalendarOff className="w-7 h-7 inline mr-3 text-[#FF6F61]" />Leave Requests
            </h1>
            <p className="text-muted-foreground mt-1">{isAdmin ? 'Manage staff leave requests' : 'Request and track your time off'}</p>
          </div>
          {isCaregiver && <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="request-leave-btn"><Plus className="w-4 h-4 mr-2" />Request Leave</Button>}
        </div>

        {balance && (
          <div className="grid grid-cols-3 gap-4">
            {['annual', 'sick', 'personal'].map(type => (
              <Card key={type} className="glass-card border-border">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground uppercase">{type}</p>
                  <p className="text-2xl font-bold text-[#00A6A0]">{balance[type]?.remaining}</p>
                  <p className="text-xs text-muted-foreground">of {balance[type]?.total} days</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'denied'].map(f => (
            <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'}
              className={filter === f ? 'bg-[#00A6A0] text-[#0F1419]' : 'border-border text-muted-foreground'}
              onClick={() => setFilter(f)}>{f.charAt(0).toUpperCase() + f.slice(1)}</Button>
          ))}
        </div>

        <Card className="glass-card border-border">
          <CardContent className="p-0">
            {loading ? <div className="p-12 text-center text-muted-foreground">Loading...</div> :
            filtered.length === 0 ? <div className="p-12 text-center text-muted-foreground">No leave requests</div> :
            <div className="divide-y divide-border">
              {filtered.map(r => (
                <div key={r.id} className="p-4 flex items-center justify-between" data-testid={`leave-${r.id}`}>
                  <div>
                    {isAdmin && <p className="text-foreground font-medium">{r.caregiver_name}</p>}
                    <p className="text-sm text-muted-foreground">{r.start_date} to {r.end_date} ({r.days} days)</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs capitalize text-muted-foreground">{r.leave_type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[r.status]}`}>{r.status}</span>
                    </div>
                    {r.reason && <p className="text-xs text-muted-foreground mt-1">{r.reason}</p>}
                  </div>
                  {isAdmin && r.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleAction(r.id, 'approve')} data-testid={`approve-leave-${r.id}`}><Check className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => handleAction(r.id, 'deny')} data-testid={`deny-leave-${r.id}`}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="glass-card border-border max-w-md">
            <DialogHeader><DialogTitle className="text-foreground">Request Leave</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Start Date</label><Input type="date" value={form.start_date} onChange={e => setForm(p => ({...p, start_date: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="leave-start-date" /></div>
                <div><label className="text-xs text-muted-foreground">End Date</label><Input type="date" value={form.end_date} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} className="bg-muted border-border text-foreground" data-testid="leave-end-date" /></div>
              </div>
              <div><label className="text-xs text-muted-foreground">Type</label>
                <Select value={form.leave_type} onValueChange={v => setForm(p => ({...p, leave_type: v}))}>
                  <SelectTrigger className="bg-muted border-border text-foreground"><SelectValue /></SelectTrigger>
                  <SelectContent className="glass-card border-border">
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="personal">Personal Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-xs text-muted-foreground">Reason</label>
                <textarea value={form.reason} onChange={e => setForm(p => ({...p, reason: e.target.value}))} placeholder="Optional reason..." className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm min-h-[60px] focus:ring-1 focus:ring-[#00A6A0] focus:outline-none" /></div>
              <Button className="w-full btn-primary" onClick={handleSubmit} data-testid="submit-leave-btn">Submit Request</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
